/**
 * Fold a live ACP session-update stream (from /api/loom/run SSE) into the loom
 * `MessageBlock[]` that the replay `BlockRenderer` already renders — so the live
 * run reuses the exact replay views instead of a parallel renderer.
 *
 * Incremental semantics (what makes "live" differ from a recorded replay):
 * - agent_message_chunk text accumulates into the trailing text block;
 * - a tool_call creates a card keyed by toolCallId; the later tool_call_update
 *   merges its result into that same card (status + output);
 * - each tool maps to its real loom block (file-search / command / knowledge-query
 *   / file-read / web-search / todo) so the live render matches loom's cards; an
 *   unmapped tool falls back to a knowledge-query-style card (name + input), never
 *   bare type text.
 *
 * Pure (no React/DOM) so it is unit-testable against captured SSE frames.
 */
import type {
  MessageBlock,
  TextBlock,
  FileSearchBlock,
  CommandBlock,
  KnowledgeQueryBlock,
  WebSearchBlock,
  FileReadBlock,
  TodoBlock,
  TodoItem,
} from "@shared/loom-message-block";

/** One ACP session update as carried in an SSE `update` frame. */
export interface AcpUpdate {
  sessionUpdate?: string;
  content?: { type?: string; text?: string }[];
  toolName?: string;
  toolCallId?: string;
  title?: string;
  status?: string;
  rawInput?: Record<string, unknown>;
}

/** Join an ACP content array into a single string. */
function textOf(content?: { text?: string }[]): string {
  return (content ?? []).map((c) => c.text ?? "").join("");
}

let counter = 0;
/** Stable-enough id for a live block (monotonic; ids are local to one run). */
function blockId(): string {
  counter += 1;
  return `live_${counter.toString(36)}`;
}

/** Map an ACP tool status to a block status. */
function blockStatus(s?: string): MessageBlock["status"] {
  if (s === "completed") return "completed";
  if (s === "failed" || s === "error") return "error";
  return "in_progress";
}

/** Read a string field from ACP rawInput (loose). */
function str(v: unknown): string {
  return typeof v === "string" ? v : "";
}

/** First non-empty string among the candidates (empty-string fallthrough). */
function pick(...vals: (string | undefined)[]): string {
  for (const v of vals) if (v !== undefined && v !== "") return v;
  return "";
}

/** Last path segment, for a file's display name. */
function baseName(p: string): string {
  const parts = p.split("/").filter(Boolean);
  return parts[parts.length - 1] ?? p;
}

/**
 * Build the loom MessageBlock for an ACP tool_call, keyed by tool name so each
 * tool renders through its REAL replay view (matching loom) instead of bare text.
 * The block's `id` IS the toolCallId, so a later tool_call_update merges into it.
 * Unmapped tools fall back to a knowledge-query-style card (name + readable input)
 * — far more legible than the raw type-name fallback.
 */
function buildToolBlock(
  toolName: string | undefined,
  id: string,
  raw: Record<string, unknown>,
  status: MessageBlock["status"],
  ts: string,
  title?: string,
): MessageBlock {
  const base = { id, status, timestamp: ts };
  switch (toolName) {
    case "FileSearch":
      return {
        ...base,
        type: "file-search",
        toolId: id,
        pattern: str(raw.pattern),
        path: typeof raw.path === "string" ? raw.path : null,
      } satisfies FileSearchBlock;
    case "FileRead":
    case "FileWrite":
    case "FileEdit": {
      const path = str(raw.file_path) || str(raw.path);
      return {
        ...base,
        type: "file-read",
        files: path ? [{ id, path, fileName: baseName(path), status: status === "completed" ? "completed" : "reading" }] : [],
        totalFiles: path ? 1 : 0,
        completedFiles: status === "completed" && path ? 1 : 0,
      } satisfies FileReadBlock;
    }
    case "BashExecution":
    case "GitExecution":
      return {
        ...base,
        type: "command",
        commandType: toolName === "GitExecution" ? "git" : "bash",
        toolId: id,
        command: pick(str(raw.command), title),
      } satisfies CommandBlock;
    case "KnowledgeQuery":
      return { ...base, type: "knowledge-query", knowledgeName: "企业知识库", query: pick(str(raw.query), str(raw.q), title) } satisfies KnowledgeQueryBlock;
    case "WebSearch":
      return { ...base, type: "web-search", query: pick(str(raw.query), str(raw.q), title) } satisfies WebSearchBlock;
    case "TodoWrite": {
      const todos: TodoItem[] = Array.isArray(raw.todos)
        ? (raw.todos as Record<string, unknown>[]).map((t) => ({
            content: str(t.content),
            status: (["pending", "in_progress", "completed"].includes(str(t.status)) ? str(t.status) : "pending") as TodoItem["status"],
            activeForm: str(t.activeForm),
          }))
        : [];
      return { ...base, type: "todo", todos } satisfies TodoBlock;
    }
    default:
      return {
        ...base,
        type: "knowledge-query",
        knowledgeName: toolName ?? "工具",
        query: title ?? Object.values(raw).map(String).join(" ").slice(0, 120),
      } satisfies KnowledgeQueryBlock;
  }
}

/**
 * Fold one ACP update into the running block list, returning a new array
 * (never mutates the input). Unknown updates pass through unchanged.
 */
export function foldAcpUpdate(blocks: MessageBlock[], u: AcpUpdate): MessageBlock[] {
  const ts = new Date().toISOString();

  if (u.sessionUpdate === "agent_message_chunk") {
    const text = textOf(u.content);
    if (text === "") return blocks;
    const last = blocks[blocks.length - 1];
    if (last && last.type === "text") {
      const merged: TextBlock = { ...last, content: last.content + text };
      return [...blocks.slice(0, -1), merged];
    }
    const tb: TextBlock = { id: blockId(), type: "text", status: "completed", timestamp: ts, content: text };
    return [...blocks, tb];
  }

  if (u.sessionUpdate === "tool_call") {
    const id = u.toolCallId ?? blockId();
    return [...blocks, buildToolBlock(u.toolName, id, u.rawInput ?? {}, blockStatus(u.status), ts, u.title)];
  }

  if (u.sessionUpdate === "tool_call_update") {
    const result = textOf(u.content);
    const status = blockStatus(u.status);
    return blocks.map((b) => {
      if (b.id !== u.toolCallId) return b; // block.id IS the toolCallId
      if (b.type === "file-search") {
        // Note: don't write `results: undefined` (exactOptionalPropertyTypes).
        const next: FileSearchBlock = { ...b, status };
        if (result !== "") next.results = [result];
        return next;
      }
      if (b.type === "command") {
        const next: CommandBlock = { ...b, status };
        if (result !== "") next.result = { output: result };
        return next;
      }
      if (b.type === "file-read") {
        return { ...b, status, completedFiles: status === "completed" ? b.totalFiles : b.completedFiles } satisfies FileReadBlock;
      }
      // knowledge-query / web-search / todo / others: settle status only.
      return { ...(b as unknown as Record<string, unknown>), status } as unknown as MessageBlock;
    });
  }

  return blocks;
}
